/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ApplicationInsightsPolicyModule
 */

import { Component, Type } from '@angular/core';
import { ApplicationInsightsPolicy } from 'object-types';

import {
    MessageItem,
    ObjectTypeItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { L10nService } from '@vmw/ngx-vip';

import {
    AviPermissionResource,
    IApplicationInsightsPolicy,
    IBotClassification,
    IBotDetectionMatch,
} from 'generated-types';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { ApplicationInsightsParamsConfigItem } from './application-insights-params.config-item.factory';
import { ApplicationSamplingConfigConfigItem } from './application-sampling-config.config-item.factory';

import * as l10n from '../application-insights-policy.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Application Insights Policy feilds to be omited.
 */
type TApplicationInsightsPolicyOmitFields = 'configpb_attributes' | 'application_insights_params' |
'application_sampling_config';

/**
 * Bot Detection fields to be omitted.
 */
type TBotDetectionMatchFields = 'classifications';

/**
 * Custom Bot Detection Type.
 */
type TBotDetectionPartial = Omit<IBotDetectionMatch, TBotDetectionMatchFields>;

/**
 * Interface for IBotDetectionMatchConfig.
 */
interface IBotDetectionMatchConfig extends TBotDetectionPartial {
    classifications: RepeatedMessageItem<MessageItem<IBotClassification>>;
}

/**
 * Custom Application Insights Policy Type.
 */
type TApplicationInsightsPolicyPartial = Omit<IApplicationInsightsPolicy, TApplicationInsightsPolicyOmitFields>;

export interface IApplicationInsightsPolicyConfig extends TApplicationInsightsPolicyPartial {
    application_insights_params?: ApplicationInsightsParamsConfigItem,
    application_sampling_config?: ApplicationSamplingConfigConfigItem,
}

/**
 * Custom Bot Detection Type.
 */
const USER_DEFINED_BOT = 'USER_DEFINED_BOT';

/**
 * @description
 *     Item class for Application Insights Policy.
 * @author Zhiqian Liu
 */
export class ApplicationInsightsPolicyItem extends withEditChildMessageItemMixin(
    withFullModalMixin(ObjectTypeItem),
) {
    public static ajsDependencies = [
        'l10nService',
    ];

    /**
     * l10n Service for internationalization.
     */
    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'applicationinsightspolicy',
            windowElement: 'lazy-load',
            params: {
                include_name: true,
            },
            permissionName: AviPermissionResource.PERMISSION_APPLICATIONINSIGHTSPOLICY,
            objectType: ApplicationInsightsPolicy,
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');
        this.l10nService.registerSourceBundles(dictionary);

        this.showConfigWarnings = true;
    }

    /**
     * Getter for the 'enable_application_insights' flag.
     */
    public get appInsightsEnabled(): boolean {
        return this.getConfig().enable_application_insights;
    }
    /**
     * Setter for the 'enable_application_insights` flag.
     */
    public set appInsightsEnabled(enableAppInsights: boolean) {
        this.getConfig().enable_application_insights = enableAppInsights;
    }

    /**
     * Getter for the learn_from_bots match property under application_insights_params.
     */
    public get learnFromBots(): MessageItem<IBotDetectionMatchConfig> {
        return this.getConfig().application_insights_params.config.learn_from_bots;
    }

    public get classifications(): RepeatedMessageItem<MessageItem<IBotClassification>> {
        return this.learnFromBots.config.classifications;
    }

    public addClassification(): void {
        this.classifications.add();
    }

    public removeClassification(classification: MessageItem<IBotClassification>): void {
        this.classifications.removeByMessageItem(classification);
    }

    /**
     * Set user_defined_type to undefined when the type of bot isn't User-defined bot.
     */
    public onClassificationTypeChange(classification: MessageItem<IBotClassification>): void {
        if (classification.config.type !== USER_DEFINED_BOT) {
            delete classification.config.user_defined_type;
        }
    }

    /**
     * Set the application_insights_params object to its default values in this.data.config.config. (Not a typo)
     */
    public setDefaultAppInsightsParams(): void {
        this.safeSetNewChildByField('application_insights_params');
    }

    /**
     * Set the application_sampling_config object to its default values in this.data.config.config. (Not a typo)
     */
    public setDefaultAppSamplingConfig(): void {
        this.safeSetNewChildByField('application_sampling_config');
    }

    /**
     * Import lazy-loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { ApplicationInsightsPolicyModalComponent } = await import(
            /* webpackChunkName: "application-insights-policy-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/application-insights-policy-modal/application-insights-policy-modal.component',
        );

        return ApplicationInsightsPolicyModalComponent as Type<Component>;
    }

    // eslint-disable-next-line class-methods-use-this
    protected override requiredFields(): string[] {
        return [
            'application_insights_params',
            'application_sampling_config',
        ];
    }

    protected override getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.applicationInsightsPolicyBreadcrumbTitle);
    }
}
